import { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import { EventContext } from "../../contexts/EventContext";

const AssignTicket = ({ open, close, user }) => {
  const { pairingEvent } = useContext(EventContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [firstName, setFirstName] = useState(user?.profile?.firstName || "");
  const [lastName, setLastName] = useState(user?.profile?.lastName || "");
  const [email, setEmail] = useState(user?.email || "");

  const [errors, setErrors] = useState({});

  const formatMoney = (cents, currency = "USD") => {
    if (typeof cents !== "number") return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const validate = () => {
    const next = {};
    if (!selectedTicketId) next.selectedTicketId = "Please select a ticket";
    if (!firstName?.trim()) next.firstName = "First name is required";
    if (!lastName?.trim()) next.lastName = "Last name is required";
    if (!email?.trim()) {
      next.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) next.email = "Please enter a valid email";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const getTickets = async () => {
    if (!pairingEvent?.event?._id) return;
    setLoading(true);
    try {
      const result = await request(
        `ticketing/tickets/availability/?eventId=${pairingEvent?.event?._id}`
      );
      if (result?.success) {
        setTickets(result.data || []);
      } else {
        toast("Unable to load tickets");
        console.log("[AssignTicket] getTickets not successful", result);
      }
    } catch (e) {
      toast("Error loading tickets");
      console.log("[AssignTicket] getTickets error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pairingEvent?.event?._id]);

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSelectedTicketId("");
    close();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const body = {
      eventId: pairingEvent?.event?._id,
      ticketTypeId: selectedTicketId,
      userData: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim()
      },
    };

    try {
      const result = await request("ticketing/assignnewticket", 'post', body);
      if (result?.success) {
        toast.success("Ticket assigned");
        if (result?.message) {
            toast.warning(result.message);
        }
        close();
      } 
    } catch (e) {
      toast.error(`Error assigning ticket: ${e.message}`);
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign Ticket</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: 1 }}
      >
        <Typography variant="subtitle2">
          The attendee will receive an email confirmation
        </Typography>
        <FormControl
          fullWidth
          error={!!errors.selectedTicketId}
          variant="outlined"
          margin="normal"
        >
          <InputLabel id="assign-ticket-select-label" shrink>
            Select Ticket
          </InputLabel>
          <Select
            labelId="assign-ticket-select-label"
            id="assign-ticket-select"
            label="Select Ticket"
            value={selectedTicketId}
            onChange={(e) => setSelectedTicketId(e.target.value)}
            disabled={loading}
          >
            {loading && (
              <MenuItem value="" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            )}
            {!loading && tickets?.length === 0 && (
              <MenuItem value="" disabled>
                No tickets available
              </MenuItem>
            )}
            {!loading &&
              tickets?.map((t) => (
                <MenuItem key={t.id} value={t.id} disabled={t.available === 0}>
                  {t.name}
                  {t.available === 0 ? " (Sold out)" : ""}
                  {typeof t.priceCents === "number" && t.currency
                    ? ` — ${formatMoney(t.priceCents, t.currency)}`
                    : ""}
                </MenuItem>
              ))}
              <MenuItem value="noTicket">Assign without ticket</MenuItem>
          </Select>
          {errors.selectedTicketId && (
            <FormHelperText>{errors.selectedTicketId}</FormHelperText>
          )}
        </FormControl>

        <TextField
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          error={!!errors.firstName}
          helperText={errors.firstName}
          fullWidth
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          error={!!errors.lastName}
          helperText={errors.lastName}
          fullWidth
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || submitting} style={{width: 100}}>
          {submitting ? <CircularProgress size={24} style={{color: 'white'}}/> : "Submit" }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTicket;
